import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from "ag-grid-enterprise";
import type { SsrmRequestBody, SsrmResponseBody } from "../ag_grid_shared/types";

export class ServerSideDatasource implements IServerSideDatasource {
  private readonly gridDataUrl: string;
  private readonly getSearchText: () => string;
  private readonly setPinnedBottomRow: (rows: unknown[]) => void;

  constructor(
    gridDataUrl: string,
    getSearchText: () => string,
    setPinnedBottomRow: (rows: unknown[]) => void
  ) {
    this.gridDataUrl = gridDataUrl;
    this.getSearchText = getSearchText;
    this.setPinnedBottomRow = setPinnedBottomRow;
  }

  getRows(params: IServerSideGetRowsParams): void {
    const request = params.request;

    const body: SsrmRequestBody = {
      startRow: request.startRow ?? 0,
      endRow: request.endRow ?? 100,
      sortModel: (request.sortModel ?? []).map((s) => ({
        colId: s.colId,
        sort: s.sort as "asc" | "desc",
      })),
      filterModel: (request.filterModel ?? {}) as SsrmRequestBody["filterModel"],
      rowGroupCols: (request.rowGroupCols ?? []).map((c) => ({
        id: c.id,
        field: c.field ?? "",
        displayName: c.displayName ?? "",
      })),
      groupKeys: request.groupKeys ?? [],
      valueCols: (request.valueCols ?? []).map((c) => ({
        id: c.id,
        field: c.field ?? "",
        aggFunc: c.aggFunc ?? "",
      })),
      pivotCols: (request.pivotCols ?? []).map((c) => ({
        id: c.id,
        field: c.field ?? "",
        displayName: c.displayName ?? "",
      })),
      pivotMode: request.pivotMode ?? false,
      searchText: this.getSearchText(),
    };

    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";

    fetch(this.gridDataUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`SSRM block request failed: ${response.status}`);
        }
        return response.json() as Promise<SsrmResponseBody>;
      })
      .then((data) => {
        params.success({
          rowData: data.rows,
          rowCount: data.lastRow,
        });

        // Pinned footer row — set after success so AG-Grid has rendered the block
        if (data.footerRow && data.footerRow.length > 0) {
          this.setPinnedBottomRow(data.footerRow);
        }
      })
      .catch((error) => {
        console.error("[ServerSideDatasource] getRows error:", error);
        params.fail();
      });
  }
}
